import { useNavigate } from "react-router-dom";
import { useAuth, apiFetch } from "../context/AuthContext.js";

const BASE_URL = process.env.REACT_APP_BASE_URL;
const LOGOUT_TIMEOUT_MS = 8000;

export const useLogout = () => {
  const navigate = useNavigate();
  const { logout: authLogout } = useAuth();

  // `timeoutMs` — how long to wait for the server to revoke the refresh
  // token. The connection screen passes a short one: the server is known to
  // be unreachable there, and signing out must not hang on it.
  const logout = async ({ timeoutMs = LOGOUT_TIMEOUT_MS } = {}) => {
    console.group("🔴 LOGOUT SEQUENCE STARTED");
    console.time("logout-duration");

    // ── Step 1: Hit the backend ──────────────────────────────────────────────
    let backendSuccess = false;
    try {
      console.log("📡 Step 1 — Calling backend logout...");

      const controller = new AbortController();
      const timeoutId = setTimeout(() => {
        controller.abort();
        console.warn("⏱️ Backend logout request timed out after", timeoutMs, "ms");
      }, timeoutMs);

      const response = await apiFetch(`${BASE_URL}/users/logout`, {
        method: "POST",
        signal: controller.signal,
        // Signing out — a failure here must not raise the connection banner.
        reportConnection: false,
      });

      clearTimeout(timeoutId);

      console.log("📡 Backend response status:", response.status);

      if (response.ok) {
        const data = await response.json().catch(() => ({}));
        console.log("✅ Backend logout succeeded:", data);
        backendSuccess = true;
      } else {
        const errData = await response.json().catch(() => ({}));
        console.warn("⚠️ Backend returned non-OK status:", response.status, errData);
      }
    } catch (err) {
      if (err.name === "AbortError") {
        console.warn("⏱️ Backend logout aborted due to timeout.");
      } else {
        console.warn("❌ Backend logout threw an error:", err.message);
      }
    }

    // ── Step 2: Clear client state regardless ───────────────────────────────
    // authLogout() runs the one shared teardown (utils/authStorage.js
    // clearAuthStorage + the session manager's cross-tab broadcast) that
    // every other logout path uses. This used to wipe ALL of localStorage
    // instead, which also threw away the device's theme and language and
    // made this logout behave differently from the idle / expired ones.
    console.log("🧹 Step 2 — Clearing session state...");
    try {
      authLogout();
      console.log("✅ authLogout() called successfully.");
    } catch (err) {
      console.error("❌ authLogout() threw:", err.message);
    }

    // ── Step 3: Navigate ─────────────────────────────────────────────────────
    console.log("🚀 Step 3 — Navigating to /login...");
    console.timeEnd("logout-duration");
    console.groupEnd();

    // Small flush delay so React state settles before navigation
    await new Promise((resolve) => setTimeout(resolve, 50));
    navigate("/login", { replace: true });

    // ── Step 4: Final safety net ─────────────────────────────────────────────
    // If navigate() somehow fails (e.g., router not mounted), force a hard redirect
    setTimeout(() => {
      if (!window.location.pathname.startsWith("/login")) {
        console.warn("🚨 navigate() may have failed — forcing hard redirect.");
        window.location.replace("/login");
      }
    }, 500);

    return { backendSuccess };
  };

  return { logout };
};